/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BulkSendApp - Message Composer Component
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file components/MessageComposer/MessageComposer.tsx
 * @version 1.0.0
 * @description Editor principal de mensajes con variables y preview
 * @created 2025-10-29 - Fase 3 Session 6
 * 
 * FEATURES:
 * - Editor de texto con soporte de variables
 * - Inserción de variables con selector
 * - Contador de caracteres en vivo
 * - Detección de emojis y enlaces
 * - Preview en tiempo real
 * - Guardado de borradores
 * - Integración con templates
 * - Análisis de riesgo en vivo
 */

import React, { useState, useRef, useEffect } from 'react';
import { Card, Form, Button, Badge, Row, Col, ButtonGroup, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faPlus,
  faSave,
  faUndo,
  faRedo,
  faFileAlt,
  faSmile,
  faLink,
  faExclamationTriangle,
  faCog,
  faTimes,
  faLock,
  faBold,
  faItalic,
  faStrikethrough,
  faCode,
  faCheck,
  faTag,
  faClock,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { getHatateToken } from '../../helpers/utils';
import { useUserContext } from '../../layouts/UserContext';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

export interface MessageVariable {
  id: string;
  name: string;
  placeholder: string;
  example: string;
  required: boolean;
  type: 'text' | 'number' | 'date' | 'currency';
}

export interface MessageTemplate {
  id: string;
  name: string;
  content: string;
  category: string;
  variables: string[];
}

interface Template {
  id: string;
  phoneId: string;
  name: string;
  category: string;
  content: string;
  variables: string[];
  metadata: {
    createdAt: string;
    updatedAt: string;
    usageCount: number;
    lastUsed: string | null;
  };
}

export interface MessageStats {
  characterCount: number;
  emojiCount: number;
  linkCount: number;
  variableCount: number;
  estimatedMessages: number;
}

export interface MessageComposerProps {
  initialMessage?: string;
  availableVariables?: MessageVariable[];
  onMessageChange: (message: string) => void;
  onStatsChange?: (stats: MessageStats) => void;
  showPreview?: boolean;
  showTemplates?: boolean;
  maxCharacters?: number;
  className?: string;
}

// ───────────────────────────────────────────────────────────────────────
// NOTIFICATION SYSTEM
// ───────────────────────────────────────────────────────────────────────

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// DEFAULT VARIABLES
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_VARIABLES: MessageVariable[] = [
  {
    id: 'nombre',
    name: 'Nombre',
    placeholder: '[nombre]',
    example: 'Juan Pérez',
    required: true,
    type: 'text'
  },
  {
    id: 'telefono',
    name: 'Teléfono',
    placeholder: '[telefono]',
    example: '+56912345678',
    required: false,
    type: 'text'
  },
  {
    id: 'curso',
    name: 'Curso',
    placeholder: '[curso]',
    example: 'React Avanzado',
    required: false,
    type: 'text'
  },
  {
    id: 'email',
    name: 'Email',
    placeholder: '[email]',
    example: 'juan@email.com',
    required: false,
    type: 'text'
  },
  {
    id: 'empresa',
    name: 'Empresa',
    placeholder: '[empresa]',
    example: 'Mi Empresa',
    required: false,
    type: 'text'
  },
  {
    id: 'fecha',
    name: 'Fecha',
    placeholder: '[fecha]',
    example: '15/02/2025',
    required: false,
    type: 'date'
  }
];

// ═══════════════════════════════════════════════════════════════════════════
// CUSTOM VARIABLE MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

interface CustomVariable extends MessageVariable {
  enabled: boolean;
  locked: boolean;
}

const SYSTEM_VARIABLES = ['nombre', 'email']; // Variables bloqueadas

// API Constants
const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
const API_KEY = 'anubis-whatsapp-api-key';

const getInitialVariables = (): CustomVariable[] => {
  const saved = localStorage.getItem('bulkSend.customVariables');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      console.error('Error loading custom variables:', e);
    }
  }
  
  // Default con todas habilitadas
  return DEFAULT_VARIABLES.map(v => ({
    ...v,
    enabled: true,
    locked: SYSTEM_VARIABLES.includes(v.id)
  }));
};

const saveCustomVariables = (variables: CustomVariable[]) => {
  localStorage.setItem('bulkSend.customVariables', JSON.stringify(variables));
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const MessageComposer: React.FC<MessageComposerProps> = ({
  initialMessage = '',
  availableVariables = DEFAULT_VARIABLES,
  onMessageChange,
  onStatsChange,
  showPreview = true,
  showTemplates = true,
  maxCharacters = 1000,
  className = ''
}) => {
  
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  
  const [message, setMessage] = useState(initialMessage);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [history, setHistory] = useState<string[]>([initialMessage]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // Templates State
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [phoneId, setPhoneId] = useState<string>('');
  // Cargar template pendiente al montar
useEffect(() => {
  const pending = localStorage.getItem('bulksend:pendingTemplate');
  if (pending) {
    try {
      const template = JSON.parse(pending);
      
      // Cargar contenido
      setMessage(template.content);
      
      // Sincronizar variables
      const templateVars = template.variables || [];
      setCustomVariables(prev => {
        const updated = [...prev];
        templateVars.forEach((varName: string) => {
          const varIndex = updated.findIndex(v => v.id === varName);
          if (varIndex !== -1) {
            updated[varIndex] = { ...updated[varIndex], enabled: true };
          }
        });
        saveCustomVariables(updated);
        return updated;
      });
      
      // Notificar
      showNotification('success', `✅ Template "${template.name}" cargado`);
      
      // Limpiar
      localStorage.removeItem('bulksend:pendingTemplate');
    } catch (e) {
      console.error('Error loading pending template:', e);
    }
  }
}, []);

// Listener de evento en tiempo real (para ventanas ya abiertas)
useEffect(() => {
  const handler = (e: Event) => {
    const customEvent = e as CustomEvent;
    const template = customEvent.detail.template;
    
    setMessage(template.content);
    
    const templateVars = template.variables || [];
    setCustomVariables(prev => {
      const updated = [...prev];
      templateVars.forEach((varName: string) => {
        const varIndex = updated.findIndex(v => v.id === varName);
        if (varIndex !== -1) {
          updated[varIndex] = { ...updated[varIndex], enabled: true };
        }
      });
      saveCustomVariables(updated);
      return updated;
    });
    
    showNotification('success', `✅ Template "${template.name}" cargado`);
  };
  
  window.addEventListener('bulksend:loadTemplate', handler);
  return () => window.removeEventListener('bulksend:loadTemplate', handler);
}, []);
  const [modalZIndex, setModalZIndex] = useState(10000);
  
  const { user } = useUserContext();
  const API_TOKEN = getHatateToken();
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [customVariables, setCustomVariables] = useState<CustomVariable[]>(getInitialVariables);
const [showVariableModal, setShowVariableModal] = useState(false);
const [showEmojiPicker, setShowEmojiPicker] = useState(false);
const [newVariableName, setNewVariableName] = useState('');
const [newVariableExample, setNewVariableExample] = useState('');
const [editingVariable, setEditingVariable] = useState<string | null>(null);
const [editValue, setEditValue] = useState('');
const [showLinkModal, setShowLinkModal] = useState(false);
const [linkUrl, setLinkUrl] = useState('');
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [variableToDelete, setVariableToDelete] = useState<string | null>(null);

// Ya tiene sistema de notificaciones, solo agregar listener
useEffect(() => {
  const handler = (e: CustomEvent) => {
    const template = e.detail.template;
    setMessage(template.content);
    // Sincronizar variables...
    showNotification('success', `✅ Template "${template.name}" cargado`);
  };
  
  window.addEventListener('bulksend:loadTemplate', handler as EventListener);
  return () => window.removeEventListener('bulksend:loadTemplate', handler as EventListener);
}, []);

const showNotification = (type: Notification['type'], message: string) => {
  const id = Date.now().toString();
  setNotifications(prev => [...prev, { id, type, message }]);
  
  // Auto-dismiss después de 3 segundos
  setTimeout(() => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, 3000);
};

  // ───────────────────────────────────────────────────────────────────────
  // TEMPLATE FUNCTIONS
  // ───────────────────────────────────────────────────────────────────────
  
  const bringModalToFront = () => {
    setModalZIndex(prev => prev + 1);
  };
  
  const loadTemplates = async () => {
    if (!phoneId) return;
    
    try {
      setLoadingTemplates(true);
      const response = await axios.get(`${API_BASE_URL}/templates`, {
        params: { phoneId },
        headers: {
          'x-api-key': API_KEY,
          'Authorization': API_TOKEN
        }
      });
      
      if (response.data.success) {
        // Filtrar templates válidos: deben tener content y metadata
        const validTemplates = (response.data.templates || []).filter((t: Template) => 
          t && 
          t.content && 
          t.content.trim() !== '' &&
          t.name &&
          t.name.trim() !== ''
        );
        setTemplates(validTemplates);
      }
    } catch (error: any) {
      showNotification('error', '❌ Error al cargar templates: ' + error.message);
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  const loadCategories = async () => {
    if (!phoneId) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/templates-categories`, {
        params: { phoneId },
        headers: {
          'x-api-key': API_KEY,
          'Authorization': API_TOKEN
        }
      });
      
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error: any) {
      console.error('Error loading categories:', error);
    }
  };
  
  const trackTemplateUsage = async (templateId: string) => {
    try {
      await axios.post(`${API_BASE_URL}/templates/${templateId}/use`, {}, {
        headers: {
          'x-api-key': API_KEY,
          'Authorization': API_TOKEN
        }
      });
    } catch (error: any) {
      console.error('Error tracking usage:', error);
    }
  };
  
  const handleOpenTemplateSelector = () => {
    if (!phoneId) {
      showNotification('error', '❌ Esperando conexión con el servidor...');
      return;
    }
    setShowTemplateModal(true);
    bringModalToFront();
    loadTemplates();
    loadCategories();
  };
  
  const handleSelectTemplate = (template: Template) => {
    // Cargar contenido del template
    setMessage(template.content);
    
    // Sincronizar variables del template con customVariables
    const templateVars = template.variables;
    setCustomVariables(prev => {
      const updated = [...prev];
      
      // Habilitar variables que están en el template
      templateVars.forEach(varName => {
        const varIndex = updated.findIndex(v => v.id === varName);
        if (varIndex !== -1) {
          updated[varIndex] = { ...updated[varIndex], enabled: true };
        }
      });
      
      saveCustomVariables(updated);
      return updated;
    });
    
    // Track usage
    trackTemplateUsage(template.id);
    
    // Cerrar modal y notificar
    setShowTemplateModal(false);
    showNotification('success', `✅ Template "${template.name}" cargado`);
  };
  
  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  // ───────────────────────────────────────────────────────────────────────
  // COMPUTED STATS
  // ───────────────────────────────────────────────────────────────────────
  
  const stats: MessageStats = React.useMemo(() => {
    const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu;
    const linkRegex = /(https?:\/\/[^\s]+)/g;
    const variableRegex = /\[([^\]]+)\]/g;
    
    const characterCount = message.length;
    const emojiCount = (message.match(emojiRegex) || []).length;
    const linkCount = (message.match(linkRegex) || []).length;
    const variableCount = (message.match(variableRegex) || []).length;
    const estimatedMessages = Math.ceil(characterCount / 160);
    
    return {
      characterCount,
      emojiCount,
      linkCount,
      variableCount,
      estimatedMessages
    };
  }, [message]);

  // ───────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────────
  
  // Cargar phoneId dinámicamente
  useEffect(() => {
    const fetchPhoneId = async () => {
      const phoneId = user._id;
      try {
        if (phoneId) {
          setPhoneId(phoneId);
        } else {
          showNotification('error', '❌ No se pudo obtener el phoneId del bot');
        }
      } catch (error: any) {
        console.error('Error fetching phoneId:', error);
        showNotification('error', '❌ Error al conectar con el servidor');
      }
    };
    
    fetchPhoneId();
  }, [user]);
  
  useEffect(() => {
    onMessageChange(message);
    onStatsChange?.(stats);
  }, [message, stats, onMessageChange, onStatsChange]);

  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMessage = e.target.value;
    
    // Check max characters
    if (newMessage.length <= maxCharacters) {
      setMessage(newMessage);
      
      // Update history
      setHistory(prev => [...prev.slice(0, historyIndex + 1), newMessage]);
      setHistoryIndex(prev => prev + 1);
    }
  };
  
  const handleCursorChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCursorPosition(e.target.selectionStart || 0);
  };
  
  const insertVariable = (variable: MessageVariable) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = 
      message.substring(0, start) + 
      variable.placeholder + 
      message.substring(end);
    
    setMessage(newMessage);
    
    // Update history
    setHistory(prev => [...prev.slice(0, historyIndex + 1), newMessage]);
    setHistoryIndex(prev => prev + 1);
    
    // Focus and set cursor after variable
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + variable.placeholder.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };
  
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
      setMessage(history[historyIndex - 1]);
    }
  };
  
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      setMessage(history[historyIndex + 1]);
    }
  };
  
  const handleClear = () => {
    setMessage('');
    setHistory(['']);
    setHistoryIndex(0);
  };
  
  const handleSaveDraft = () => {
  localStorage.setItem('bulkSend.messageDraft', message);
  showNotification('success', '✅ Borrador guardado correctamente');
};
  
  const handleLoadDraft = () => {
    const draft = localStorage.getItem('bulkSend.messageDraft');
    if (draft) {
      setMessage(draft);
      setHistory(prev => [...prev, draft]);
      setHistoryIndex(prev => prev + 1);
    }
  };

  // ───────────────────────────────────────────────────────────────────────
// VARIABLE MANAGEMENT
// ───────────────────────────────────────────────────────────────────────

const handleToggleVariable = (variableId: string) => {
  const updated = customVariables.map(v => 
    v.id === variableId && !v.locked
      ? { ...v, enabled: !v.enabled }
      : v
  );
  setCustomVariables(updated);
  saveCustomVariables(updated);
};

const handleAddVariable = () => {
  if (!newVariableName.trim()) {
  showNotification('warning', '⚠️ Ingresa un nombre para la variable');
  return;
}
  
  const id = newVariableName.toLowerCase().replace(/\s+/g, '_');
  const placeholder = `[${id}]`;
  
  // Verificar si ya existe
  if (customVariables.some(v => v.id === id)) {
  showNotification('error', '❌ Esta variable ya existe');
  return;
}
  
  const newVar: CustomVariable = {
    id,
    name: newVariableName,
    placeholder,
    example: newVariableExample || 'Ejemplo',
    required: false,
    type: 'text',
    enabled: true,
    locked: false
  };
  
  const updated = [...customVariables, newVar];
  setCustomVariables(updated);
  saveCustomVariables(updated);
  
  // Limpiar form
  setNewVariableName('');
  setNewVariableExample('');
  
  showNotification('success', '✅ Variable agregada correctamente');
};

const handleRemoveVariable = (variableId: string) => {
  if (SYSTEM_VARIABLES.includes(variableId)) {
    showNotification('warning', '🔒 Esta variable está bloqueada y no se puede eliminar');
    return;
  }
  
  // Abrir modal de confirmación
  setVariableToDelete(variableId);
  setShowDeleteConfirm(true);
};

const confirmDeleteVariable = () => {
  if (!variableToDelete) return;
  
  const updated = customVariables.filter(v => v.id !== variableToDelete);
  setCustomVariables(updated);
  saveCustomVariables(updated);
  
  showNotification('success', '✅ Variable eliminada correctamente');
  
  // Limpiar y cerrar
  setVariableToDelete(null);
  setShowDeleteConfirm(false);
};

const cancelDeleteVariable = () => {
  setVariableToDelete(null);
  setShowDeleteConfirm(false);
};

const handleEditVariable = (variableId: string, newExample: string) => {
  const updated = customVariables.map(v => 
    v.id === variableId
      ? { ...v, example: newExample }
      : v
  );
  setCustomVariables(updated);
  saveCustomVariables(updated);
};

// ───────────────────────────────────────────────────────────────────────
// EMOJI INSERTION
// ───────────────────────────────────────────────────────────────────────

const insertEmoji = (emoji: string) => {
  const textarea = textareaRef.current;
  if (!textarea) return;
  
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const newMessage = 
    message.substring(0, start) + 
    emoji + 
    message.substring(end);
  
  setMessage(newMessage);
  setShowEmojiPicker(false);
  
  // Focus and set cursor after emoji
  setTimeout(() => {
    textarea.focus();
    const newPosition = start + emoji.length;
    textarea.setSelectionRange(newPosition, newPosition);
  }, 0);
};

// ───────────────────────────────────────────────────────────────────────
// TEXT FORMATTING
// ───────────────────────────────────────────────────────────────────────

const insertFormatting = (format: 'bold' | 'italic' | 'strike' | 'mono') => {
  const textarea = textareaRef.current;
  if (!textarea) return;
  
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = message.substring(start, end);
  
  let wrapper = '';
  switch (format) {
    case 'bold':
      wrapper = '*';
      break;
    case 'italic':
      wrapper = '_';
      break;
    case 'strike':
      wrapper = '~';
      break;
    case 'mono':
      wrapper = '```';
      break;
  }
  
  let formattedText = '';
  if (selectedText) {
    // Hay texto seleccionado
    formattedText = `${wrapper}${selectedText}${wrapper}`;
  } else {
    // No hay selección, insertar marcadores
    formattedText = `${wrapper}texto${wrapper}`;
  }
  
  const newMessage = 
    message.substring(0, start) + 
    formattedText + 
    message.substring(end);
  
  setMessage(newMessage);
  
  // Update history
  setHistory(prev => [...prev.slice(0, historyIndex + 1), newMessage]);
  setHistoryIndex(prev => prev + 1);
  
  // Focus and select formatted text
  setTimeout(() => {
    textarea.focus();
    if (selectedText) {
      textarea.setSelectionRange(start + wrapper.length, start + wrapper.length + selectedText.length);
    } else {
      textarea.setSelectionRange(start + wrapper.length, start + wrapper.length + 5); // seleccionar "texto"
    }
  }, 0);
};

const insertLink = () => {
  setShowLinkModal(true);
};

const confirmInsertLink = () => {
  if (!linkUrl.trim()) {
    showNotification('warning', '⚠️ Ingresa una URL válida');
    return;
  }
  
  // Validar formato URL básico
  if (!linkUrl.startsWith('http://') && !linkUrl.startsWith('https://')) {
    showNotification('warning', '⚠️ La URL debe comenzar con http:// o https://');
    return;
  }
  
  const textarea = textareaRef.current;
  if (!textarea) return;
  
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  
  const newMessage = 
    message.substring(0, start) + 
    linkUrl + 
    message.substring(end);
  
  setMessage(newMessage);
  
  // Update history
  setHistory(prev => [...prev.slice(0, historyIndex + 1), newMessage]);
  setHistoryIndex(prev => prev + 1);
  
  setTimeout(() => {
    textarea.focus();
    const newPosition = start + linkUrl.length;
    textarea.setSelectionRange(newPosition, newPosition);
  }, 0);
  
  showNotification('success', '🔗 Enlace insertado correctamente');
  
  // Limpiar y cerrar
  setLinkUrl('');
  setShowLinkModal(false);
};

const POPULAR_EMOJIS = [
  '😀', '😃', '😄', '😁', '😊', '😍', '🥰', '😘',
  '👍', '👏', '🙌', '🤝', '💪', '🎉', '🎊', '✨',
  '❤️', '💙', '💚', '💛', '🧡', '💜', '🖤', '🤍',
  '⭐', '🌟', '✅', '❌', '⚠️', '📌', '📍', '🔥'
];

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: TOOLBAR
  // ───────────────────────────────────────────────────────────────────────
  
  const renderToolbar = () => {
    return (
  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
    {/* Left: Undo/Redo */}
    <ButtonGroup size="sm">
      <Button 
        variant="outline-secondary"
        onClick={handleUndo}
        disabled={historyIndex === 0}
        title="Deshacer"
      >
        <FontAwesomeIcon icon={faUndo} />
      </Button>
      <Button 
        variant="outline-secondary"
        onClick={handleRedo}
        disabled={historyIndex === history.length - 1}
        title="Rehacer"
      >
        <FontAwesomeIcon icon={faRedo} />
      </Button>
    </ButtonGroup>
    
    {/* Center: Emoji Picker */}
    <div style={{ position: 'relative' }}>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
        title="Insertar Emoji"
      >
        <FontAwesomeIcon icon={faSmile} />
      </Button>
      
      {/* Emoji Picker Dropdown */}
      {showEmojiPicker && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '8px',
          backgroundColor: 'var(--bs-body-bg)',
          border: '1px solid var(--bs-border-color)',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          width: '280px'
        }}>
          <div className="d-flex flex-wrap gap-1">
            {POPULAR_EMOJIS.map((emoji, idx) => (
              <button
                key={idx}
                onClick={() => insertEmoji(emoji)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {emoji}
              </button>
            ))}
          </div>
          <div style={{ 
            marginTop: '8px', 
            paddingTop: '8px', 
            borderTop: '1px solid var(--bs-border-color)',
            fontSize: '0.75rem',
            color: 'var(--bs-secondary)',
            textAlign: 'center'
          }}>
            Click para insertar
          </div>
        </div>
      )}
    </div>

    {/* Center: Format Toolbar */}
<ButtonGroup size="sm">
  <Button
    variant="outline-secondary"
    onClick={() => insertFormatting('bold')}
    title="Negrita (*texto*)"
  >
    <FontAwesomeIcon icon={faBold} />
  </Button>
  <Button
    variant="outline-secondary"
    onClick={() => insertFormatting('italic')}
    title="Cursiva (_texto_)"
  >
    <FontAwesomeIcon icon={faItalic} />
  </Button>
  <Button
    variant="outline-secondary"
    onClick={() => insertFormatting('strike')}
    title="Tachado (~texto~)"
  >
    <FontAwesomeIcon icon={faStrikethrough} />
  </Button>
  <Button
    variant="outline-secondary"
    onClick={() => insertFormatting('mono')}
    title="Monoespaciado (```texto```)"
  >
    <FontAwesomeIcon icon={faCode} />
  </Button>
  <Button
    variant="outline-secondary"
    onClick={insertLink}
    title="Insertar enlace"
  >
    <FontAwesomeIcon icon={faLink} />
  </Button>
</ButtonGroup>

{/* Emoji Picker */}
    
    {/* Right: Draft + Variable Manager */}
    <div className="d-flex gap-2">
      <Button 
        variant="outline-info"
        size="sm"
        onClick={() => setShowVariableModal(true)}
        title="Gestionar Variables"
      >
        <FontAwesomeIcon icon={faCog} className="me-1" />
        Variables
      </Button>
      <Button 
        variant="outline-primary"
        size="sm"
        onClick={handleLoadDraft}
      >
        <FontAwesomeIcon icon={faFileAlt} className="me-1" />
        Borrador
      </Button>
      <Button 
        variant="outline-success"
        size="sm"
        onClick={handleSaveDraft}
      >
        <FontAwesomeIcon icon={faSave} />
      </Button>
    </div>
  </div>
);
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: EDITOR
  // ───────────────────────────────────────────────────────────────────────
  
  const renderEditor = () => {
    const isNearLimit = stats.characterCount > maxCharacters * 0.9;
    const isOverLimit = stats.characterCount > maxCharacters;
    
    return (
      <div>
        <Form.Group className="mb-3">
          <Form.Label style={{ fontWeight: 500, marginBottom: '8px' }}>
            <FontAwesomeIcon icon={faPen} className="me-2" />
            Mensaje Personalizado
          </Form.Label>
          <Form.Control
            ref={textareaRef}
            as="textarea"
            rows={8}
            placeholder="Escribe tu mensaje aquí... Usa las variables disponibles para personalizar."
            value={message}
            onChange={handleMessageChange}
            onSelect={handleCursorChange}
            style={{ 
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontFamily: 'inherit',
              backgroundColor: 'var(--bs-body-bg)',
              color: 'var(--bs-body-color)',
              border: `1px solid ${isOverLimit ? 'var(--bs-danger)' : 'var(--bs-border-color)'}`
            }}
          />
        </Form.Group>
        
        {/* Stats Bar */}
        <div className="d-flex flex-wrap gap-3 align-items-center">
          {/* Character Count */}
          <div style={{ fontSize: '0.85rem' }}>
            <Badge 
              bg={isOverLimit ? 'danger' : isNearLimit ? 'warning' : 'secondary'}
              style={{ padding: '6px 10px' }}
            >
              {stats.characterCount} / {maxCharacters} caracteres
            </Badge>
          </div>
          
          {/* Emoji Count */}
          {stats.emojiCount > 0 && (
            <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
              <FontAwesomeIcon icon={faSmile} className="me-1" />
              {stats.emojiCount} emoji{stats.emojiCount !== 1 ? 's' : ''}
            </div>
          )}
          
          {/* Link Count */}
          {stats.linkCount > 0 && (
            <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
              <FontAwesomeIcon icon={faLink} className="me-1" />
              {stats.linkCount} enlace{stats.linkCount !== 1 ? 's' : ''}
            </div>
          )}
          
          {/* Variable Count */}
          {stats.variableCount > 0 && (
            <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)' }}>
              📝 {stats.variableCount} variable{stats.variableCount !== 1 ? 's' : ''}
            </div>
          )}
          
          {/* Estimated Messages */}
          <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary)', marginLeft: 'auto' }}>
            ~{stats.estimatedMessages} mensaje{stats.estimatedMessages !== 1 ? 's' : ''} SMS
          </div>
        </div>
        
        {/* Warning for risk */}
        {(stats.emojiCount > 3 || stats.linkCount > 1) && (
          <div className="mt-2">
            <Badge bg="warning" style={{ fontSize: '0.8rem', padding: '6px 10px' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              Alto uso de emojis/enlaces puede aumentar el riesgo de bloqueo
            </Badge>
          </div>
        )}
      </div>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: VARIABLE PALETTE
  // ───────────────────────────────────────────────────────────────────────
  
  const renderVariablePalette = () => {
  // Filtrar solo variables habilitadas
  const enabledVariables = customVariables.filter(v => v.enabled);
  
  return (
    <Card style={{
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)'
    }}>
      <Card.Header style={{
        backgroundColor: 'var(--bs-body-bg)',
        borderBottom: '1px solid var(--bs-border-color)',
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h6 className="mb-0" style={{ fontWeight: 600 }}>
          Variables Disponibles
        </h6>
        <Badge bg="primary" style={{ fontSize: '0.75rem' }}>
          {enabledVariables.length}
        </Badge>
      </Card.Header>
      <Card.Body style={{ padding: '16px' }}>
        {enabledVariables.length > 0 ? (
          <>
            <div className="d-flex flex-wrap gap-2">
              {enabledVariables.map(variable => {
  const canDelete = variable.id !== 'nombre';
  
  return (
    <div
      key={variable.id}
      style={{ 
        position: 'relative',
        display: 'inline-block'
      }}
      className="variable-hover-container"
    >
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => insertVariable(variable)}
        title={`Ejemplo: ${variable.example}`}
        style={{ 
          borderRadius: '8px',
          fontSize: '0.85rem',
          paddingRight: canDelete ? '32px' : undefined
        }}
      >
        <FontAwesomeIcon icon={faPlus} className="me-1" />
        {variable.placeholder}
      </Button>
      
      {/* Delete Button - Solo si NO es [nombre] */}
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveVariable(variable.id);
          }}
          className="variable-delete-btn"
          style={{
            position: 'absolute',
            right: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: '#dc3545',
            color: 'white',
            fontSize: '0.7rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s',
            padding: 0,
            lineHeight: 1
          }}
          title="Eliminar variable"
        >
          ×
        </button>
      )}
      
      <style>
        {`
          .variable-hover-container:hover .variable-delete-btn {
            opacity: 1;
          }
          .variable-delete-btn:hover {
            opacity: 1 !important;
            backgroundColor: #c82333;
          }
        `}
      </style>
    </div>
  );
})}
            </div>
            
            <div className="mt-3" style={{ fontSize: '0.8rem', color: 'var(--bs-secondary)' }}>
              <strong>Tip:</strong> Haz clic en una variable para insertarla en el cursor
            </div>
          </>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px',
            color: 'var(--bs-secondary)' 
          }}>
            <p>No hay variables habilitadas</p>
            <Button 
  variant="outline-info"
  size="sm"
  onClick={() => {
    console.log('🔧 Botón Variables clickeado');
    console.log('Estado actual:', showVariableModal);
    setShowVariableModal(true);
    console.log('Nuevo estado:', true);
  }}
  title="Gestionar Variables"
>
              Gestionar Variables
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

  // ───────────────────────────────────────────────────────────────────────
// RENDER: PREVIEW PANEL
// ───────────────────────────────────────────────────────────────────────

const renderPreviewPanel = () => {
  // Procesar mensaje reemplazando variables
  const processedMessage = React.useMemo(() => {
  if (!message) return '';
  
  let processed = message;
  
  // Reemplazar dinámicamente con los ejemplos actuales de customVariables
  customVariables.forEach(variable => {
    const regex = new RegExp(variable.placeholder.replace(/[[\]]/g, '\\$&'), 'gi');
    processed = processed.replace(regex, variable.example);
  });
  
  return processed;
}, [message, customVariables]);

// Renderizar con formato HTML
const renderFormattedMessage = (text: string) => {
  if (!text) return null;
  
  // Convertir formato WhatsApp a HTML
  let formatted = text
    // Negrita: *texto* → <strong>texto</strong>
    .replace(/\*([^\*]+)\*/g, '<strong>$1</strong>')
    // Cursiva: _texto_ → <em>texto</em>
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Tachado: ~texto~ → <del>texto</del>
    .replace(/~([^~]+)~/g, '<del>$1</del>')
    // Monoespaciado: ```texto``` → <code>texto</code>
    .replace(/```([^`]+)```/g, '<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px;">$1</code>')
    // Enlaces: detectar URLs
    .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" style="color:#0084ff;text-decoration:underline;" target="_blank">$1</a>');
  
  return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
};

  return (
    <Card style={{
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)',
      marginBottom: '16px'
    }}>
      <Card.Header style={{
        backgroundColor: 'var(--bs-body-bg)',
        borderBottom: '1px solid var(--bs-border-color)',
        padding: '16px'
      }}>
        <h6 className="mb-0" style={{ fontWeight: 600 }}>
          📱 Preview WhatsApp
        </h6>
      </Card.Header>
      <Card.Body style={{ 
        padding: '20px', 
        backgroundColor: '#e5ddd5',
        minHeight: '200px'
      }}>
        {/* Burbuja estilo WhatsApp */}
        <div style={{ 
          backgroundColor: '#dcf8c6', 
          padding: '8px 12px', 
          borderRadius: '8px 8px 0 8px',
          fontSize: '0.95rem',
          color: '#000',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          position: 'relative',
          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
          maxWidth: '85%',
          marginLeft: 'auto'
        }}>
          {processedMessage ? renderFormattedMessage(processedMessage) : (
  <span style={{ color: '#999' }}>
    Escribe algo en el editor para ver el preview...
  </span>
)}
          
          {/* Timestamp estilo WhatsApp */}
          {processedMessage && (
            <div style={{
              fontSize: '0.7rem',
              color: '#667781',
              textAlign: 'right',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '4px'
            }}>
              {new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
              <span style={{ color: '#4fc3f7' }}>✓✓</span>
            </div>
          )}
        </div>
        
        {/* Info de variables detectadas */}
        {stats.variableCount > 0 && (
          <div style={{ 
            marginTop: '12px',
            fontSize: '0.8rem',
            color: 'var(--bs-secondary)',
            textAlign: 'center'
          }}>
            ✨ {stats.variableCount} variable{stats.variableCount !== 1 ? 's' : ''} procesada{stats.variableCount !== 1 ? 's' : ''}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// ───────────────────────────────────────────────────────────────────────
// RENDER: VARIABLE MANAGEMENT MODAL
// ───────────────────────────────────────────────────────────────────────

const renderVariableModal = () => {
  if (!showVariableModal) return null;
  
  return (
    <>
      {/* Backdrop con blur estilo Apple */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99998,
          animation: 'fadeIn 0.15s ease-out'
        }}
        onClick={() => setShowVariableModal(false)}
      />
      
      {/* Modal Window */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '650px',
          maxHeight: '85vh',
          backgroundColor: 'var(--bs-body-bg)',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '0.5px solid rgba(0, 0, 0, 0.1)',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'modalSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header minimalista */}
        <div style={{
          padding: '18px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '0.5px solid rgba(0, 0, 0, 0.08)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.4rem' }}>⚙️</span>
            <div>
              <h5 className="mb-0" style={{ 
                fontWeight: 600,
                color: 'var(--bs-body-color)',
                fontSize: '1.1rem',
                letterSpacing: '-0.015em'
              }}>
                Variables
              </h5>
            </div>
          </div>
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowVariableModal(false)}
            style={{ 
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              color: 'var(--bs-body-color)',
              fontSize: '1.1rem',
              fontWeight: '500',
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.06)';
            }}
          >
            ×
          </Button>
        </div>
        
        {/* Body con scroll */}
        <div style={{ 
          flex: 1,
          overflow: 'auto',
          padding: '24px'
        }}>
          {/* Lista de Variables */}
          <div className="mb-4">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h6 style={{ 
                fontWeight: 600,
                marginBottom: 0,
                fontSize: '0.9rem',
                color: 'var(--bs-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Actuales
              </h6>
              <Badge 
                style={{ 
                  fontSize: '0.75rem',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  backgroundColor: '#25D366',
                  color: 'white',
                  fontWeight: 600
                }}
              >
                {customVariables.filter(v => v.enabled).length} activas
              </Badge>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {customVariables.map(variable => {
                const isEditing = editingVariable === variable.id;
                
                return (
                  <div 
                    key={variable.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 16px',
                      backgroundColor: variable.enabled 
                        ? 'rgba(37, 211, 102, 0.06)' 
                        : 'var(--bs-body-bg)',
                      border: variable.enabled
                        ? '1px solid rgba(37, 211, 102, 0.2)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                      borderRadius: '12px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        marginBottom: '4px' 
                      }}>
                        <span style={{
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          fontFamily: 'SF Mono, Monaco, monospace',
                          color: variable.enabled ? '#075E54' : 'var(--bs-secondary)'
                        }}>
                          {variable.placeholder}
                        </span>
                        {variable.locked && (
                          <span style={{
                            fontSize: '0.7rem',
                            padding: '2px 7px',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(255, 193, 7, 0.15)',
                            color: '#F57C00',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}>
                            <FontAwesomeIcon icon={faLock} style={{ fontSize: '0.65rem' }} />
                            Sistema
                          </span>
                        )}
                      </div>
                      
                      {/* Edición de ejemplo */}
                      {isEditing ? (
                        <div className="d-flex gap-2 align-items-center" style={{ marginTop: '6px' }}>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="Nuevo ejemplo"
                            style={{ 
                              maxWidth: '250px',
                              borderRadius: '8px',
                              fontSize: '0.85rem',
                              border: '1px solid rgba(0, 0, 0, 0.12)'
                            }}
                            autoFocus
                          />
                          <Button
                            size="sm"
                            onClick={() => {
                              handleEditVariable(variable.id, editValue);
                              setEditingVariable(null);
                              showNotification('success', '✅ Ejemplo actualizado');
                            }}
                            style={{ 
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              backgroundColor: '#25D366',
                              border: 'none',
                              color: 'white',
                              fontWeight: 600
                            }}
                          >
                            ✓
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setEditingVariable(null)}
                            style={{ 
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              backgroundColor: 'rgba(0, 0, 0, 0.06)',
                              border: 'none',
                              color: 'var(--bs-body-color)',
                              fontWeight: 600
                            }}
                          >
                            ✕
                          </Button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-2">
                          <small style={{ 
                            color: 'var(--bs-secondary)',
                            fontSize: '0.82rem'
                          }}>
                            {variable.example}
                          </small>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                              setEditingVariable(variable.id);
                              setEditValue(variable.example);
                            }}
                            style={{ 
                              padding: 0,
                              fontSize: '0.85rem',
                              textDecoration: 'none',
                              opacity: 0.5,
                              transition: 'opacity 0.15s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                            title="Editar ejemplo"
                          >
                            ✏️
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    <div className="d-flex gap-2 align-items-center">
                      {/* Toggle Enable/Disable */}
                      <Form.Check
                        type="switch"
                        id={`switch-${variable.id}`}
                        checked={variable.enabled}
                        onChange={() => handleToggleVariable(variable.id)}
                        style={{ 
                          transform: 'scale(1.1)',
                          cursor: 'pointer'
                        }}
                        title="Habilitar/Deshabilitar"
                      />
                      
                      {/* Remove Button */}
                      {!variable.locked && (
                        <Button
                          size="sm"
                          onClick={() => handleRemoveVariable(variable.id)}
                          style={{ 
                            width: '30px',
                            height: '30px',
                            padding: 0,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s',
                            backgroundColor: 'rgba(220, 53, 69, 0.1)',
                            border: '1px solid rgba(220, 53, 69, 0.2)',
                            color: '#dc3545'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc3545';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                            e.currentTarget.style.color = '#dc3545';
                          }}
                          title="Eliminar variable"
                        >
                          <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.85rem' }} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Agregar Nueva Variable */}
          <div 
            style={{
              padding: '20px',
              backgroundColor: 'rgba(37, 211, 102, 0.04)',
              borderRadius: '12px',
              border: '1px dashed rgba(37, 211, 102, 0.3)'
            }}
          >
            <h6 style={{ 
              fontWeight: 600,
              marginBottom: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.95rem',
              color: '#075E54'
            }}>
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: '0.9rem' }} />
              Nueva Variable
            </h6>
            
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--bs-secondary)' }}>
                Nombre
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: pagina_web, descuento"
                value={newVariableName}
                onChange={(e) => setNewVariableName(e.target.value)}
                style={{ 
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  border: '1px solid rgba(0, 0, 0, 0.12)'
                }}
              />
              {newVariableName && (
                <small style={{ 
                  color: '#25D366',
                  display: 'block',
                  marginTop: '6px',
                  fontWeight: 500,
                  fontFamily: 'SF Mono, Monaco, monospace',
                  fontSize: '0.8rem'
                }}>
                  → [{newVariableName.toLowerCase().replace(/\s+/g, '_')}]
                </small>
              )}
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--bs-secondary)' }}>
                Ejemplo
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: www.ejemplo.cl, 20%"
                value={newVariableExample}
                onChange={(e) => setNewVariableExample(e.target.value)}
                style={{ 
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  border: '1px solid rgba(0, 0, 0, 0.12)'
                }}
              />
            </Form.Group>
            
            <Button
              onClick={handleAddVariable}
              style={{ 
                borderRadius: '10px',
                width: '100%',
                padding: '10px',
                fontWeight: 600,
                fontSize: '0.9rem',
                backgroundColor: '#25D366',
                border: 'none',
                color: 'white',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#128C7E';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#25D366';
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Agregar
            </Button>
          </div>
          
          {/* Info Box */}
          <div 
            style={{
              marginTop: '16px',
              padding: '14px 16px',
              backgroundColor: 'rgba(0, 132, 255, 0.06)',
              border: '1px solid rgba(0, 132, 255, 0.15)',
              borderRadius: '12px',
              fontSize: '0.82rem',
              display: 'flex',
              gap: '10px',
              lineHeight: '1.5'
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>💡</span>
            <div style={{ flex: 1 }}>
              <code style={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                padding: '2px 5px',
                borderRadius: '4px',
                fontWeight: 600,
                fontSize: '0.8rem'
              }}>[nombre]</code> y{' '}
              <code style={{ 
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                padding: '2px 5px',
                borderRadius: '4px',
                fontWeight: 600,
                fontSize: '0.8rem'
              }}>[email]</code> son obligatorias. 
              Puedes editar sus ejemplos pero no eliminarlas.
            </div>
          </div>
        </div>
        
        {/* Footer minimalista */}
        <div style={{
          padding: '14px 24px',
          borderTop: '0.5px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: 'var(--bs-body-bg)',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <Button
            onClick={() => setShowVariableModal(false)}
            style={{ 
              borderRadius: '10px',
              padding: '8px 24px',
              fontWeight: 600,
              fontSize: '0.9rem',
              backgroundColor: '#25D366',
              border: 'none',
              color: 'white',
              transition: 'all 0.15s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#128C7E';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#25D366';
            }}
          >
            Listo
          </Button>
        </div>
      </div>
      
      {/* Animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes modalSlideIn {
            from {
              opacity: 0;
              transform: translate(-50%, -48%) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1);
            }
          }
        `}
      </style>
    </>
  );
};

// ───────────────────────────────────────────────────────────────────────
// RENDER: LINK MODAL
// ───────────────────────────────────────────────────────────────────────

const renderLinkModal = () => {
  if (!showLinkModal) return null;
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={() => {
        setShowLinkModal(false);
        setLinkUrl('');
      }}
    >
      <Card 
        style={{
          width: '90%',
          maxWidth: '500px',
          margin: '20px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Card.Header style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '1px solid var(--bs-border-color)',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h5 className="mb-0" style={{ fontWeight: 600 }}>
            <FontAwesomeIcon icon={faLink} className="me-2" />
            Insertar Enlace
          </h5>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setShowLinkModal(false);
              setLinkUrl('');
            }}
            style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0 }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </Card.Header>
        
        <Card.Body style={{ padding: '24px' }}>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 500 }}>
              URL del Enlace
            </Form.Label>
            <Form.Control
              type="url"
              placeholder="https://ejemplo.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  confirmInsertLink();
                }
              }}
              autoFocus
              style={{ borderRadius: '8px' }}
            />
            <small style={{ color: 'var(--bs-secondary)', display: 'block', marginTop: '8px' }}>
              💡 La URL debe comenzar con http:// o https://
            </small>
          </Form.Group>
          
          <div className="d-flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowLinkModal(false);
                setLinkUrl('');
              }}
              style={{ borderRadius: '8px', flex: 1 }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={confirmInsertLink}
              style={{ borderRadius: '8px', flex: 1 }}
            >
              <FontAwesomeIcon icon={faLink} className="me-2" />
              Insertar
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────
// RENDER: DELETE CONFIRMATION MODAL
// ───────────────────────────────────────────────────────────────────────

const renderDeleteConfirmModal = () => {
  if (!showDeleteConfirm || !variableToDelete) return null;
  
  const variable = customVariables.find(v => v.id === variableToDelete);
  if (!variable) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          animation: 'fadeIn 0.15s ease-out'
        }}
        onClick={cancelDeleteVariable}
      />
      
      {/* Modal pequeño estilo iOS */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '320px',
          backgroundColor: 'var(--bs-body-bg)',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '0.5px solid rgba(0, 0, 0, 0.1)',
          zIndex: 9999999,
          overflow: 'hidden',
          animation: 'modalSlideIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Contenido */}
        <div style={{ padding: '24px 20px 20px' }}>
          {/* Icono */}
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '1.8rem'
          }}>
            ⚠️
          </div>
          
          {/* Título */}
          <h6 style={{
            fontWeight: 600,
            fontSize: '1.05rem',
            textAlign: 'center',
            marginBottom: '10px',
            color: 'var(--bs-body-color)'
          }}>
            ¿Eliminar variable?
          </h6>
          
          {/* Descripción */}
          <p style={{
            fontSize: '0.85rem',
            textAlign: 'center',
            color: 'var(--bs-secondary)',
            marginBottom: 0,
            lineHeight: '1.5'
          }}>
            La variable{' '}
            <code style={{
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              padding: '2px 5px',
              borderRadius: '4px',
              fontWeight: 600
            }}>
              {variable.placeholder}
            </code>{' '}
            será eliminada permanentemente.
          </p>
        </div>
        
        {/* Botones */}
        <div style={{
          borderTop: '0.5px solid rgba(0, 0, 0, 0.08)',
          display: 'flex'
        }}>
          {/* Cancelar */}
          <button
            onClick={cancelDeleteVariable}
            style={{
              flex: 1,
              padding: '14px',
              border: 'none',
              backgroundColor: 'transparent',
              color: 'var(--bs-body-color)',
              fontSize: '0.9rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.15s',
              borderRight: '0.5px solid rgba(0, 0, 0, 0.08)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Cancelar
          </button>
          
          {/* Eliminar */}
          <button
            onClick={confirmDeleteVariable}
            style={{
              flex: 1,
              padding: '14px',
              border: 'none',
              backgroundColor: 'transparent',
              color: '#dc3545',
              fontSize: '0.9rem',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'background-color 0.15s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Eliminar
          </button>
        </div>
      </div>
    </>
  );
};

// ───────────────────────────────────────────────────────────────────────
// RENDER: TEMPLATE SELECTOR MODAL (APPLE AQUA STYLE)
// ───────────────────────────────────────────────────────────────────────

const renderTemplateModal = () => {
  if (!showTemplateModal) return null;
  
  return (
    <>
      {/* Backdrop con blur */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: modalZIndex,
          animation: 'modalBackdropFadeIn 0.3s ease-out'
        }}
        onClick={() => setShowTemplateModal(false)}
      />
      
      {/* Modal Window */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '85vh',
          backgroundColor: 'var(--bs-body-bg)',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          zIndex: modalZIndex + 1,
          overflow: 'hidden',
          animation: 'modalSlideIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => {
          e.stopPropagation();
          bringModalToFront();
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--bs-border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'var(--bs-body-bg)',
          flexShrink: 0
        }}>
          <h5 style={{ margin: 0, fontWeight: 600, fontSize: '1.25rem' }}>
            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
            Seleccionar Template
          </h5>
          <button
            onClick={() => setShowTemplateModal(false)}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--bs-secondary)',
              cursor: 'pointer',
              padding: '0',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
              e.currentTarget.style.color = 'var(--bs-body-color)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--bs-secondary)';
            }}
          >
            ×
          </button>
        </div>
        
        {/* Category Filter */}
        <div style={{ 
          padding: '16px 24px', 
          borderBottom: '1px solid var(--bs-border-color)',
          backgroundColor: 'var(--bs-body-bg)',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Badge
              bg={selectedCategory === 'all' ? 'primary' : 'light'}
              text={selectedCategory === 'all' ? 'white' : 'dark'}
              style={{
                padding: '8px 16px',
                fontSize: '0.85rem',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSelectedCategory('all')}
            >
              Todos ({templates.length})
            </Badge>
            {(categories || []).map(cat => {
              const count = templates.filter(t => t.category === cat).length;
              return (
                <Badge
                  key={cat}
                  bg={selectedCategory === cat ? 'primary' : 'light'}
                  text={selectedCategory === cat ? 'white' : 'dark'}
                  style={{
                    padding: '8px 16px',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                    textTransform: 'capitalize'
                  }}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat} ({count})
                </Badge>
              );
            })}
          </div>
        </div>
        
        {/* Body - Template List */}
        <div style={{ 
          padding: '24px', 
          overflowY: 'auto',
          flex: 1
        }}>
          {loadingTemplates ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px'
            }}>
              <Spinner animation="border" variant="primary" />
              <p style={{ margin: 0, color: 'var(--bs-secondary)' }}>
                Cargando templates...
              </p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px',
              color: 'var(--bs-secondary)'
            }}>
              <FontAwesomeIcon icon={faFileAlt} size="3x" style={{ marginBottom: '16px', opacity: 0.3 }} />
              <p style={{ fontSize: '1rem', marginBottom: '8px' }}>
                No hay templates disponibles
              </p>
              <small style={{ fontSize: '0.85rem' }}>
                {selectedCategory === 'all' 
                  ? 'Crea tu primer template en la pestaña Templates'
                  : 'No hay templates en esta categoría'}
              </small>
            </div>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '16px'
            }}>
              {filteredTemplates.map(template => (
                <div
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  style={{
                    padding: '16px',
                    border: '1px solid var(--bs-border-color)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: 'var(--bs-body-bg)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--bs-primary)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.15)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--bs-border-color)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Header */}
                  <div style={{ marginBottom: '12px' }}>
                    <h6 style={{ 
                      margin: 0, 
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      marginBottom: '6px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {template.name}
                    </h6>
                    <Badge 
                      bg="light" 
                      text="dark" 
                      style={{ 
                        fontSize: '0.7rem',
                        padding: '4px 8px',
                        textTransform: 'capitalize',
                        fontWeight: 500
                      }}
                    >
                      <FontAwesomeIcon icon={faTag} className="me-1" style={{ fontSize: '0.65rem' }} />
                      {template.category}
                    </Badge>
                  </div>
                  
                  {/* Content Preview */}
                  <p style={{ 
                    fontSize: '0.8rem',
                    color: 'var(--bs-secondary)',
                    marginBottom: '12px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4'
                  }}>
                    {template.content}
                  </p>
                  
                  {/* Footer Stats */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.7rem',
                    color: 'var(--bs-secondary)',
                    paddingTop: '8px',
                    borderTop: '1px solid rgba(0,0,0,0.06)'
                  }}>
                    <span>
                      <FontAwesomeIcon icon={faChartLine} className="me-1" />
                      {template.metadata?.usageCount || 0} usos
                    </span>
                    {template.variables && template.variables.length > 0 && (
                      <span>
                        {template.variables.length} variable{template.variables.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <style>{`
        @keyframes modalBackdropFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1) translateY(0);
          }
        }
      `}</style>
    </>
  );
};

// ───────────────────────────────────────────────────────────────────────
// RENDER: NOTIFICATIONS
// ───────────────────────────────────────────────────────────────────────

const renderNotifications = () => {
  if (notifications.length === 0) return null;
  
  const getColorByType = (type: Notification['type']) => {
    switch (type) {
      case 'success': return { bg: '#10b981', border: '#059669' };
      case 'error': return { bg: '#ef4444', border: '#dc2626' };
      case 'warning': return { bg: '#f59e0b', border: '#d97706' };
      case 'info': return { bg: '#3b82f6', border: '#2563eb' };
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      maxWidth: '400px'
    }}>
      {notifications.map(notification => {
        const colors = getColorByType(notification.type);
        return (
          <div
            key={notification.id}
            style={{
              backgroundColor: colors.bg,
              border: `2px solid ${colors.border}`,
              borderRadius: '12px',
              padding: '16px 20px',
              color: 'white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              fontSize: '0.95rem',
              fontWeight: 500,
              animation: 'slideInRight 0.3s ease-out',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            <span>{notification.message}</span>
            <button
              onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: 0,
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                opacity: 0.8,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
            >
              ×
            </button>
          </div>
        );
      })}
      
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

// Cerrar emoji picker al hacer click fuera
useEffect(() => {
  const handleClickOutside = (e: MouseEvent) => {
    if (showEmojiPicker) {
      const target = e.target as HTMLElement;
      if (!target.closest('button') && !target.closest('[data-emoji-picker]')) {
        setShowEmojiPicker(false);
      }
    }
  };
  
  document.addEventListener('click', handleClickOutside);
  return () => document.removeEventListener('click', handleClickOutside);
}, [showEmojiPicker]);

  // ───────────────────────────────────────────────────────────────────────
  // RENDER: MAIN
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <div className={className}>
      <Row className="g-4">
        {/* Editor Column */}
        <Col lg={8}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            <Card.Header style={{
              backgroundColor: 'var(--bs-body-bg)',
              borderBottom: '1px solid var(--bs-border-color)',
              padding: '16px 20px'
            }}>
              <h5 className="mb-0" style={{ fontWeight: 600 }}>
                <FontAwesomeIcon icon={faPen} className="me-2" />
                Editor de Mensaje
              </h5>
            </Card.Header>
            <Card.Body style={{ padding: '24px' }}>
              {renderToolbar()}
              {renderEditor()}
            </Card.Body>
          </Card>
        </Col>
        
        {/* Sidebar Column */}
<Col lg={4}>
  {/* Preview Panel - PRIMERO */}
  {showPreview && renderPreviewPanel()}
  
  {/* Variables Panel */}
  {renderVariablePalette()}
  
  {/* Templates Button - ACTIVADO FASE 5 */}
  {showTemplates && (
    <div className="mt-3">
      <Button
        variant="outline-primary"
        className="w-100"
        onClick={handleOpenTemplateSelector}
        style={{ 
          borderRadius: '8px',
          fontWeight: 500,
          padding: '12px'
        }}
      >
        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
        Cargar desde Template
      </Button>
    </div>
  )}
</Col>
{/* Modal de Variables */}
{renderVariableModal()}
{/* Modal de Link */}
{renderLinkModal()}
{/* Modal de Confirmación Delete */}
{renderDeleteConfirmModal()}
{/* Modal de Templates - FASE 5 */}
{renderTemplateModal()}
{/* Notifications */}
{renderNotifications()}
      </Row>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export default MessageComposer;
export { DEFAULT_VARIABLES };